// ═══════════════════════════════════════════════════════════════════════════
// feedReport.js — UrbanPiper's Order Transactions Report, read in the browser
// for the history upload (contract 5.11; plan Phase 1a; plan 7.5 item 11;
// appendix 7 "History and backfill" and customer flow step 11; appendix 6
// correction 14).
// ═══════════════════════════════════════════════════════════════════════════
// Pure: no React, no network, no storage, no logging. components/FeedImport.js
// reads the file (CSV through lib/csv.js parseCSVGrid, XLSX through
// read-excel-file, loaded on demand) and hands this module a grid of cells.
//
// • Privacy by construction: each row is PROJECTED field by field from the
//   allow-listed report columns in REPORT_COLUMNS. The raw row is never spread
//   or kept, so a customer column (name, phone, address, city, customer id,
//   payment transaction id, rider, notes) never leaves parseReport(). Customer
//   headers are recognised only to say, by NAME, that they were removed. In
//   a CSV, a line whose cells don't line up with the header (an unquoted
//   comma would move part of an address into an allow-listed column) is
//   skipped unread.
// • Headers: the real export's names are unverified (contract open question
//   3; UrbanPiper's help page is prose), so they match tolerantly (case,
//   spacing and punctuation ignored, a leading BOM stripped) against the
//   aliases below, and EXACTLY: "Store ID" never satisfies "ID", "Total
//   Amount" isn't "Sub-total Amount", "Discount" isn't "Aggregator Discount".
//   Correct the table when a real export arrives.
// • Money (plan 7.3): gross = Σ Sub-total Amount; net = Σ (Sub-total −
//   Discount), never also the aggregator/merchant split (uploaded as check
//   fields). Merchant Total and Total Amount are never sales. Which orders
//   count: all but Cancelled; a cancelled row is still uploaded (an import
//   can cancel an order, contract 5.11) but not counted. A fully discounted
//   order counts like any other and is only warned about (owner, 21 Oct
//   2026). All of this is preview arithmetic for display: the server rolls
//   the real day totals up from its ledger, and calcDaily is untouched.
// • Time: the report's own timestamps, never the upload time. A Created At
//   without an offset stays a wall-clock string ("YYYY-MM-DDTHH:MM:SS") in the
//   time zone the uploader picks (contract open question 2); one with an
//   offset is an instant. Never new Date(string). A date with no time of day
//   is left out, exactly as the .NET host's own report reader leaves it out
//   (docs/integrations/FEED_ALIGN_STATUS.md section 1, item 22): read as
//   midnight, a trading day cutoff would book it on the day before.
// • Rows the host would refuse before anything else (no platform code, no
//   readable subtotal: item 5) are left out here, so the preview and "Orders
//   to upload" show what the server takes.
// ═══════════════════════════════════════════════════════════════════════════
import { normPlatformCode, isValidTimeZone, isDay, hhmm } from "./feedUi";

// = feedApi IMPORT_MAX_ROWS (contract 5.11); a test keeps the two in step.
export const MAX_CHUNK_ROWS = 500;
// Under the import route's 1 MB body limit (contract 3), with room to spare.
export const MAX_CHUNK_BYTES = 900000;
// The wire row (feedApi importRow) has longer snake_case keys and a nested
// ext_order_refs object, so each row is budgeted this much on top of its
// camelCase JSON.
const ROW_WIRE_OVERHEAD = 160;
// A report this big is refused before it is read.
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
// Title rows above the header are tolerated this far down.
const HEADER_SCAN_ROWS = 20;

const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

// ── Headers ─────────────────────────────────────────────────────────────────
// The same normaliser as lib/csv.js pick(), plus a leading BOM.
export function normHeader(h) {
  if (h == null) return "";
  return String(h).replace(/^\uFEFF/, "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

// field → accepted header spellings (normalised). The field names are the
// camelCase row fields feedApi.importOrderChunk documents.
export const REPORT_COLUMNS = Object.freeze({
  providerOrderId:    Object.freeze(["id", "orderid", "urbanpiperorderid"]),
  orderRefId:         Object.freeze(["orderrefid", "orderref", "orderreferenceid"]),
  externalPlatformId: Object.freeze(["externalplatformid", "platformorderid", "aggregatororderid"]),
  platformCode:       Object.freeze(["channel", "platform", "aggregator"]),
  orderCreatedAt:     Object.freeze(["createdat", "created", "orderdate", "ordertime", "placedat", "orderplacedat"]),
  // Never a bare "State": that can be the customer's address state (an Indian
  // export may have one before Order State), which must not be uploaded and
  // would hide the order's real state. CUSTOMER_PATTERNS labels it instead.
  state:              Object.freeze(["orderstate", "status", "orderstatus"]),
  paymentMode:        Object.freeze(["paymentmode", "payment", "paymenttype"]),
  orderSubtotal:      Object.freeze(["subtotalamount", "subtotal", "ordersubtotal", "itemtotal"]),
  discount:           Object.freeze(["discount", "totaldiscount", "discountamount"]),
  aggregatorDiscount: Object.freeze(["aggregatordiscount"]),
  merchantDiscount:   Object.freeze(["merchantdiscount"]),
  totalCharges:       Object.freeze(["charges", "totalcharges"]),
  totalTaxes:         Object.freeze(["totaltaxes", "taxes", "tax"]),
  orderTotal:         Object.freeze(["totalamount", "ordertotal"]),
  providerStoreId:    Object.freeze(["storeid", "locationid", "outletid"]),
  posStoreRef:        Object.freeze(["storerefid", "storeref", "merchantrefid", "posid"]),
  providerBrandId:    Object.freeze(["brandid"]),
  storeName:          Object.freeze(["storename", "store", "outlet", "locationname"]),
  brandName:          Object.freeze(["brandname", "brand"]),
  orderType:          Object.freeze(["fulfillmentmode", "fulfilmentmode", "ordertype"]),
  deliveryType:       Object.freeze(["deliverytype"]),
});
const ALIAS_TO_FIELD = new Map();
for (const [field, aliases] of Object.entries(REPORT_COLUMNS)) {
  for (const a of aliases) ALIAS_TO_FIELD.set(a, field);
}
// The columns a report must have to be recognised at all.
export const REQUIRED_FIELDS = Object.freeze(["providerOrderId", "orderCreatedAt", "providerStoreId"]);
// The report's own header names for those, as shown in "not recognised".
export const REQUIRED_HEADERS = Object.freeze(["ID", "Created At", "Store ID"]);

// Used only to LABEL dropped headers; anything not in REPORT_COLUMNS is
// dropped either way. Tested against normalised header names.
export const CUSTOMER_PATTERNS = Object.freeze([
  /customer/, /phone/, /mobile/, /email/, /address/, /city/,
  /pincode|postcode|postalcode|zipcode|^zip$/, /^lat$|latitude|^lng$|^lon$|^long$|longitude|geo/,
  /transactionid|paymenttransaction|txnid|^txn/, /otp/,
  /rider|driver|deliveryexecutive|deliveryperson|deliverypartner/,
  /instruction|note|comment|message|remark/, /^name$|firstname|lastname|fullname|username/,
  /landmark|locality|street|building|flat/,
  // Address parts named on their own. Anchored, so "Order State" and "Order
  // Status" (allow-listed) never match.
  /^(state|province|region|county|emirate)$/,
]);
export function isCustomerHeader(h) {
  const n = normHeader(h);
  return n !== "" && CUSTOMER_PATTERNS.some((re) => re.test(n));
}

const cellsOf = (row) => (Array.isArray(row) ? row : []);
const rawHeader = (cell) => (cell == null ? "" : String(cell).replace(/^\uFEFF/, "").trim());

// Index of the header row (the first row, among the first few, naming an
// order ID, Created At and Store ID column), or -1. XLSX exports may carry
// title rows above it.
export function findHeaderRow(grid) {
  const rows = Array.isArray(grid) ? grid : [];
  const limit = Math.min(rows.length, HEADER_SCAN_ROWS);
  for (let i = 0; i < limit; i++) {
    const fields = new Set(cellsOf(rows[i]).map((c) => ALIAS_TO_FIELD.get(normHeader(c))).filter(Boolean));
    if (REQUIRED_FIELDS.every((f) => fields.has(f))) return i;
  }
  return -1;
}

// { columns: [{ index, field }], customer: [header], unused: [header] }.
// The first column naming a field wins; a second one is unused. Customer
// and unused headers are reported by name only, never by value.
export function mapColumns(headerCells) {
  const columns = [];
  const taken = new Set();
  const customer = [];
  const unused = [];
  cellsOf(headerCells).forEach((cell, index) => {
    const raw = rawHeader(cell);
    if (!raw) return;
    const field = ALIAS_TO_FIELD.get(normHeader(raw));
    if (field && !taken.has(field)) {
      taken.add(field);
      columns.push({ index, field });
    } else if (isCustomerHeader(raw)) {
      customer.push(raw);
    } else {
      unused.push(raw);
    }
  });
  return { columns, customer, unused };
}

// ── Cells ───────────────────────────────────────────────────────────────────
function cellString(v) {
  if (typeof v === "string") {
    const s = v.trim();
    return s === "" ? null : s;
  }
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return null;   // blanks, booleans, dates: no text a report field holds
}
const isDateObject = (v) => Object.prototype.toString.call(v) === "[object Date]";
function isBlankCell(v) {
  return v == null || (typeof v === "string" && v.trim() === "");
}
export function isBlankRow(row) {
  return cellsOf(row).every(isBlankCell);
}

// Money as a number, or null when blank or unreadable: never 0 for a blank,
// never a string (feedApi refuses a string). Tolerates a currency sign or
// code, thousands separators, "(12.00)" or a minus for negatives, and the
// "1.234,50" decimal comma.
export function parseMoney(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  let s = v.replace(/\s+/g, "");
  if (!s) return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) {
    neg = true;
    s = s.slice(1, -1);
  }
  const m = s.match(/^([+-]?)([^\d.,+-]*)([+-]?)([\d.,]*\d[\d.,]*)([^\d.,]*)$/);
  if (!m) return null;
  const [, sign1, , sign2, digits, suffix] = m;
  if (sign1 && sign2) return null;
  if (sign1 === "-" || sign2 === "-" || suffix === "-") neg = true;
  let num = digits;
  const lastComma = num.lastIndexOf(",");
  const lastDot = num.lastIndexOf(".");
  if (lastComma >= 0 && lastDot >= 0) {
    // Both: the later one is the decimal separator.
    num = lastComma > lastDot ? num.replace(/\./g, "").replace(",", ".") : num.replace(/,/g, "");
  } else if (lastComma >= 0) {
    // Only commas: "12,50" is a decimal comma; "1,234" and "1,234,567" are thousands.
    num = /^\d+,\d{1,2}$/.test(num) ? num.replace(",", ".") : num.replace(/,/g, "");
  } else if ((num.match(/\./g) || []).length > 1) {
    num = num.replace(/\./g, "");   // "1.234.567": thousands
  }
  if (!/^(\d+(\.\d*)?|\.\d+)$/.test(num)) return null;
  const n = Number(num);
  if (!Number.isFinite(n)) return null;
  return neg ? -n || 0 : n;
}

// ── Created At ──────────────────────────────────────────────────────────────
const pad = (n, w = 2) => String(n).padStart(w, "0");
const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
const TIME = String.raw`(\d{1,2}):(\d{2})(?::(\d{2})(?:[.,]\d+)?)?\s*([ap]\.?m\.?)?`;
const SEP = String.raw`(?:\s*T\s*|\s*,\s*|\s+)`;
const YMD_RE = new RegExp(String.raw`^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})${SEP}${TIME}(.*)$`, "i");
const DMY_RE = new RegExp(String.raw`^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})${SEP}${TIME}(.*)$`, "i");
const D_MON_Y_RE = new RegExp(String.raw`^(\d{1,2})[\s-]+([a-z]{3,9})\.?[\s,-]+(\d{4}|\d{2})${SEP}${TIME}(.*)$`, "i");
const MON_D_Y_RE = new RegExp(String.raw`^([a-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})${SEP}${TIME}(.*)$`, "i");
const WEEKDAY_RE = /^(?:mon|tue|wed|thu|fri|sat|sun)[a-z]*\.?,?\s+/i;
const DMY_HEAD_RE = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})(?!\d)/;
const SERIAL_RE = /^\d{5}(?:\.\d+)?$/;
const OFFSET_RE = /(?:Z|[+-]\d{2}:\d{2})$/;
const NORMAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})?$/;

function validParts(y, mo, d, h, mi, s) {
  if (!(y >= 2000 && y <= 2100 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31)) return false;
  if (!(h >= 0 && h <= 23 && mi >= 0 && mi <= 59 && s >= 0 && s <= 59)) return false;
  const back = new Date(Date.UTC(y, mo - 1, d));
  return back.getUTCMonth() === mo - 1 && back.getUTCDate() === d;
}
function hour24(h, ampm) {
  if (!ampm) return h;
  if (h < 1 || h > 12) return -1;
  return (h % 12) + (/^p/i.test(ampm) ? 12 : 0);
}
// "" (no offset), "Z", "+HH:MM", or null (something unreadable after the time).
function offsetOf(tail) {
  const t = (tail || "").trim();
  if (!t) return "";
  if (/^(z|utc|gmt)$/i.test(t)) return "Z";
  const m = t.match(/^(?:utc|gmt)?\s*([+-])(\d{1,2})(?::?(\d{2}))?$/i);
  if (!m) return null;
  const hh = Number(m[2]);
  const mm = m[3] ? Number(m[3]) : 0;
  if (hh > 14 || mm > 59) return null;
  return hh === 0 && mm === 0 ? "Z" : `${m[1]}${pad(hh)}:${pad(mm)}`;
}
function build(y, mo, d, h, mi, s, ampm, tail) {
  const hh = hour24(h, ampm);
  if (!validParts(y, mo, d, hh, mi, s)) return null;
  const off = offsetOf(tail);
  if (off == null) return null;
  return `${pad(y, 4)}-${pad(mo)}-${pad(d)}T${pad(hh)}:${pad(mi)}:${pad(s)}${off}`;
}
const fullYear = (y) => (y < 100 ? 2000 + y : y);
const DAY_MS = 86400000;
// A wall-clock time held in a Date's UTC fields, or an Excel serial day.
function fromUtcFields(ms) {
  const d = new Date(Math.round(ms / 1000) * 1000);
  if (Number.isNaN(d.getTime())) return null;
  return build(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), null, "");
}
// An XLSX date cell at exactly 00:00:00 is a date with no time of day, as the
// host's reader treats it (a real midnight order is rare; the CSV keeps its time).
function fromDateCell(ms) {
  if (!Number.isFinite(ms) || ((ms % DAY_MS) + DAY_MS) % DAY_MS === 0) return null;
  return fromUtcFields(ms);
}
// A whole serial number is a date with no time of day, as the host reads it.
function fromSerial(n) {
  if (!(n >= 36526 && n < 73051)) return null;   // 2000-01-01 to 2100-01-01
  if (n === Math.floor(n)) return null;
  return fromUtcFields(Math.round((n - 25569) * 86400) * 1000);
}

// "YYYY-MM-DDTHH:MM:SS", the same followed by "Z" or "±HH:MM" when the value
// carries an offset, or null. A date with no time is null: the server needs
// the time to apply the location's trading day cutoff. That includes an XLSX
// date cell at exactly 00:00:00 and a whole serial number, as in the host's
// reader; text that names its time ("2026-11-04 00:00") keeps it.
// • a Date (an XLSX date cell): its UTC fields hold the sheet's wall clock;
// • an Excel serial day (an unformatted XLSX date cell);
// • "2026-11-04 19:22[:05]", "2026-11-04T19:22:05Z", "2026/11/04 07:22 PM";
// • "04/11/2026 19:22", "04-11-2026", "04.11.26 7:22 pm": day first unless
//   the day can only be the second number, or dayFirst is false;
// • "04 Nov 2026 19:22", "4-Nov-2026, 7:22 PM", "Nov 4, 2026 19:22",
//   optionally after a weekday ("Wed, 04 Nov 2026 19:22").
export function normaliseCreatedAt(value, { dayFirst = true } = {}) {
  if (isDateObject(value)) return fromDateCell(value.getTime());
  if (typeof value === "number") return Number.isFinite(value) ? fromSerial(value) : null;
  if (typeof value !== "string") return null;
  const s = value.trim().replace(WEEKDAY_RE, "");
  if (!s) return null;
  if (SERIAL_RE.test(s)) return fromSerial(Number(s));
  let m = s.match(YMD_RE);
  if (m) return build(+m[1], +m[2], +m[3], +m[4], +m[5], +(m[6] || 0), m[7], m[8]);
  m = s.match(DMY_RE);
  if (m) {
    const a = +m[1];
    const b = +m[2];
    const y = fullYear(+m[3]);
    let day;
    let month;
    if (a > 12 && b <= 12) [day, month] = [a, b];
    else if (b > 12 && a <= 12) [day, month] = [b, a];
    else [day, month] = dayFirst ? [a, b] : [b, a];
    return build(y, month, day, +m[4], +m[5], +(m[6] || 0), m[7], m[8]);
  }
  m = s.match(D_MON_Y_RE);
  if (m) {
    const mo = MONTHS[m[2].slice(0, 3).toLowerCase()];
    return mo ? build(fullYear(+m[3]), mo, +m[1], +m[4], +m[5], +(m[6] || 0), m[7], m[8]) : null;
  }
  m = s.match(MON_D_Y_RE);
  if (m) {
    const mo = MONTHS[m[1].slice(0, 3).toLowerCase()];
    return mo ? build(+m[3], mo, +m[2], +m[4], +m[5], +(m[6] || 0), m[7], m[8]) : null;
  }
  return null;
}

// Which order the file's numeric dates use: "dmy" or "mdy" when some value
// proves it, "ambiguous" when no value does (04/11/2026 could be either),
// "mixed" when values prove both, "none" without such dates.
export function detectDateOrder(values) {
  let dmy = false;
  let mdy = false;
  let ambiguous = false;
  for (const v of Array.isArray(values) ? values : []) {
    if (typeof v !== "string") continue;
    const m = v.trim().replace(WEEKDAY_RE, "").match(DMY_HEAD_RE);
    if (!m) continue;
    const a = +m[1];
    const b = +m[2];
    if (a > 12 && b <= 12) dmy = true;
    else if (b > 12 && a <= 12) mdy = true;
    else if (a !== b) ambiguous = true;
  }
  if (dmy && mdy) return "mixed";
  if (dmy) return "dmy";
  if (mdy) return "mdy";
  return ambiguous ? "ambiguous" : "none";
}

// ── Reading the grid ────────────────────────────────────────────────────────
const MONEY_FIELDS = new Set(["orderSubtotal", "discount", "aggregatorDiscount", "merchantDiscount", "totalCharges", "totalTaxes", "orderTotal"]);

function createdAtCell(v) {
  if (isDateObject(v) || typeof v === "number") return normaliseCreatedAt(v);
  return cellString(v);
}

// One data row → the allow-listed fields, nothing else. createdAt is the
// Created At cell, normalised later by classifyRows (it may need dayFirst).
export function projectRow(cells, columns) {
  const row = {
    providerOrderId: null, orderRefId: null, externalPlatformId: null, platformCode: null, createdAt: null,
    state: null, paymentMode: null, orderSubtotal: null, discount: null, aggregatorDiscount: null,
    merchantDiscount: null, totalCharges: null, totalTaxes: null, orderTotal: null, providerStoreId: null,
    posStoreRef: null, providerBrandId: null, storeName: null, brandName: null, orderType: null, deliveryType: null,
  };
  const c = cellsOf(cells);
  for (const { index, field } of Array.isArray(columns) ? columns : []) {
    const v = c[index];
    if (field === "orderCreatedAt") row.createdAt = createdAtCell(v);
    else if (field === "platformCode") row.platformCode = normPlatformCode(cellString(v) || "") || null;
    else if (MONEY_FIELDS.has(field)) row[field] = parseMoney(v);
    else if (has(row, field)) row[field] = cellString(v);
  }
  return row;
}

// ── Lines that don't line up with the header (CSV only) ────────────────────
// A comma or line break inside a value that isn't quoted (a hand-edited
// address, say) gives its line more or fewer cells than the others, and every
// later cell lands a column off: part of a customer column could reach an
// allow-listed field. Such a line is skipped BEFORE it is projected, so none
// of its cells is read. A data line must have the file's usual cell count and
// nothing after the header's last named column. The usual count is the
// header's, unless most data lines agree on another one with nothing after the
// named columns (a trailing comma on every data line, or a header ending in
// empty cells the lines leave out). A short line is skipped too: a field was
// lost, so the cells after it moved left. XLSX cells are structured, so the
// check is CSV only (and read-excel-file doesn't promise padded rows).
function filledWidth(cells) {
  let n = cells.length;
  while (n > 0 && isBlankCell(cells[n - 1])) n -= 1;
  return n;
}
function usualWidth(headerCells, lines) {
  const named = filledWidth(headerCells);
  const counts = new Map();
  for (const cells of lines) {
    if (cells.length < named || filledWidth(cells) > named) continue;
    counts.set(cells.length, (counts.get(cells.length) || 0) + 1);
  }
  let width = headerCells.length;
  let most = counts.get(width) || 0;
  for (const [w, n] of counts) {
    if (n > most) {
      width = w;
      most = n;
    }
  }
  return width;
}

// The grid (rows of cells, from CSV or XLSX) → { ok: true, headerRow,
// rows: [{ fileRow, ...projectRow }], skipped: [{ fileRow, reason }],
// customerColumns, unusedColumns } or { ok: false, error: "empty" |
// "no_header" | "no_rows" }. fileRow is the 1-based row number in the sheet.
// Fully blank rows are ignored. strictWidth (CSV): lines that don't line up
// with the header are skipped as "cell_count_mismatch", never projected.
export function parseReport(grid, { strictWidth = false } = {}) {
  const rows = Array.isArray(grid) ? grid : [];
  const headerIndex = findHeaderRow(rows);
  if (headerIndex < 0) return { ok: false, error: rows.some((r) => !isBlankRow(r)) ? "no_header" : "empty" };
  const headerCells = cellsOf(rows[headerIndex]);
  const { columns, customer, unused } = mapColumns(headerCells);
  const lines = [];
  for (let i = headerIndex + 1; i < rows.length; i++) {
    if (!isBlankRow(rows[i])) lines.push({ fileRow: i + 1, cells: cellsOf(rows[i]) });
  }
  const named = filledWidth(headerCells);
  const width = strictWidth ? usualWidth(headerCells, lines.map((l) => l.cells)) : null;
  const out = [];
  const skipped = [];
  for (const { fileRow, cells } of lines) {
    if (strictWidth && (cells.length !== width || filledWidth(cells) > named)) {
      skipped.push({ fileRow, reason: "cell_count_mismatch" });
      continue;
    }
    out.push({ fileRow, ...projectRow(cells, columns) });
  }
  // Every line skipped still answers ok, so the screen can say why.
  if (out.length === 0 && skipped.length === 0) return { ok: false, error: "no_rows" };
  return { ok: true, headerRow: headerIndex + 1, rows: out, skipped, customerColumns: customer, unusedColumns: unused };
}

// read-excel-file's [{ sheet, data }] → the first sheet that has a report
// header (else the first sheet, so parseReport says what's wrong).
export function pickSheet(sheets) {
  const list = Array.isArray(sheets) ? sheets.filter((s) => s && Array.isArray(s.data)) : [];
  const hit = list.find((s) => findHeaderRow(s.data) >= 0);
  return (hit || list[0] || { data: [] }).data;
}

// ── Which rows go up ────────────────────────────────────────────────────────
// Client-side reasons a row is left out. All but scientific_id and
// cell_count_mismatch are the server's own rejection reasons (contract 5.11;
// the last two are the .NET host's, FEED_ALIGN_STATUS.md section 1 item 5), so
// the server would refuse those rows anyway. cell_count_mismatch comes from
// parseReport (CSV only), the rest from classifyRows.
export const SKIP_REASONS = Object.freeze([
  "missing_order_id", "missing_store_id", "invalid_created_at", "duplicate_in_request", "scientific_id", "cell_count_mismatch",
  "missing_platform_code", "missing_subtotal",
]);
// A spreadsheet app rewrote a long number ("1.23457E+18"): its digits are lost.
const SCIENTIFIC_RE = /^[+-]?\d+(?:\.\d+)?e[+-]?\d+$/i;

export function isCancelled(state) {
  return typeof state === "string" && /^cancell?ed$/i.test(state.trim());
}

// { kept: [row + { orderCreatedAt, hasOffset, counting }], skipped: [{ fileRow, reason }] }.
// The same ID twice: the first row kept is used. Cancelled rows are KEPT
// (uploaded) with counting false. The checks run in the host's order (its
// ImportRows.Build): order ID, store ID, Created At, the same ID again, then
// the platform code and the subtotal, without which the server can neither
// route nor count an order. One deliberate difference: a row left out here
// never claims its ID. ImportRows.Build adds the ID to its seen set in the
// same-ID check, before the platform code and subtotal checks, so there a row
// rejected for either has claimed its ID and a later row with that ID is a
// duplicate_in_request. Here the unreadable row is never sent, so the host
// receives only the later readable row, and imports it.
export function classifyRows(rows, { dayFirst = true } = {}) {
  const kept = [];
  const skipped = [];
  const seen = new Set();
  for (const r of Array.isArray(rows) ? rows : []) {
    let reason = null;
    let orderCreatedAt = null;
    if (!r.providerOrderId) reason = "missing_order_id";
    else if (SCIENTIFIC_RE.test(r.providerOrderId)) reason = "scientific_id";
    else if (!r.providerStoreId) reason = "missing_store_id";
    else if (SCIENTIFIC_RE.test(r.providerStoreId)) reason = "scientific_id";
    else {
      orderCreatedAt = normaliseCreatedAt(r.createdAt, { dayFirst });
      if (!orderCreatedAt) reason = "invalid_created_at";
      else if (seen.has(r.providerOrderId)) reason = "duplicate_in_request";
      else if (!r.platformCode) reason = "missing_platform_code";
      else if (r.orderSubtotal == null) reason = "missing_subtotal";
    }
    if (reason) {
      skipped.push({ fileRow: r.fileRow, reason });
      continue;
    }
    seen.add(r.providerOrderId);
    kept.push({ ...r, orderCreatedAt, hasOffset: OFFSET_RE.test(orderCreatedAt), counting: !isCancelled(r.state) });
  }
  return { kept, skipped };
}

// ── Time zones (display only) ───────────────────────────────────────────────
const formatters = new Map();
function zoneFormatter(zone) {
  let f = formatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    formatters.set(zone, f);
  }
  return f;
}
// An instant → the wall clock in `zone`, as UTC milliseconds.
export function wallInZone(instantMs, zone) {
  const p = {};
  for (const { type, value } of zoneFormatter(zone).formatToParts(new Date(instantMs))) p[type] = value;
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
}
// A wall clock in `zone` (as UTC milliseconds) → the instant. Times in a
// daylight saving gap or overlap resolve to one of the two candidates.
export function wallToInstant(wallMs, zone) {
  const first = wallMs - (wallInZone(wallMs, zone) - wallMs);
  const second = wallMs - (wallInZone(first, zone) - first);
  return second;
}
const ymdOf = (ms) => {
  const d = new Date(ms);
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};
function cutoffMinutes(cutoff) {
  const c = hhmm(cutoff);
  return c ? Number(c.slice(0, 2)) * 60 + Number(c.slice(3, 5)) : 0;
}

// The business day ("YYYY-MM-DD") an order lands on, as the server routes it
// (plan 7.3): the order time in the location's zone, minus a day when it's
// before the location's trading day cutoff. A wall-clock time is in
// requestZone (the zone picked for the upload); with no location zone, or no
// valid requestZone, it is taken as already local. Null when unreadable.
export function businessDay(orderCreatedAt, { zone = null, cutoff = null, requestZone = null } = {}) {
  const m = typeof orderCreatedAt === "string" ? orderCreatedAt.match(NORMAL_RE) : null;
  if (!m) return null;
  const wallMs = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  const req = isValidTimeZone(requestZone) ? requestZone.trim() : null;
  let localMs = wallMs;
  if (m[7]) {
    const offsetMin = m[7] === "Z" ? 0 : (m[7][0] === "-" ? -1 : 1) * (Number(m[7].slice(1, 3)) * 60 + Number(m[7].slice(4, 6)));
    const at = zone || req;
    if (at) localMs = wallInZone(wallMs - offsetMin * 60000, at);
  } else if (zone && req && req !== zone) {
    localMs = wallInZone(wallToInstant(wallMs, req), zone);
  }
  const cut = cutoffMinutes(cutoff);
  if (cut > 0 && Math.floor(localMs / 60000) % 1440 < cut) localMs -= 86400000;
  return ymdOf(localMs);
}

// ── Routing a row to its location and feed start ────────────────────────────
const storeKey = (storeId, brandId) => `${storeId || ""}\n${brandId || ""}`;

// Where each row lands, from the setup screen's own data: stores (feedApi
// listStores) give the location; the location's channels give this
// connection's route for the row's platform code, with its feed start.
// feed_from lives on each route, not on the connection. A row with no route
// (a store not mapped yet, or a platform code not routed) takes the earliest
// feed start of this connection, the safe side; with no feed start anywhere
// nothing is in a feed window.
export function annotateRows(kept, { connectionId = null, stores = [], workspaces = [], timeZone = null } = {}) {
  const wsById = new Map();
  for (const w of Array.isArray(workspaces) ? workspaces : []) if (w && w.id) wsById.set(w.id, w);
  const storeWs = new Map();
  for (const s of Array.isArray(stores) ? stores : []) {
    if (s && s.providerStoreId) storeWs.set(storeKey(s.providerStoreId, s.providerBrandId), s.workspaceId || null);
  }
  const routes = new Map();
  let earliest = null;
  for (const w of wsById.values()) {
    for (const ch of Array.isArray(w.channels) ? w.channels : []) {
      const map = ch && ch.channelMap;
      if (!map || map.connectionId !== connectionId) continue;
      const from = isDay(map.feedFrom) ? map.feedFrom : null;
      routes.set(`${w.id}\n${normPlatformCode(map.platformCode)}`, from);
      if (from && (!earliest || from < earliest)) earliest = from;
    }
  }
  return (Array.isArray(kept) ? kept : []).map((r) => {
    const key = storeKey(r.providerStoreId, r.providerBrandId);
    const wsId = storeWs.get(key) || null;
    const ws = wsId ? wsById.get(wsId) || null : null;
    const zone = ws && isValidTimeZone(ws.timeZone) ? ws.timeZone.trim() : null;
    const day = businessDay(r.orderCreatedAt, { zone, cutoff: ws ? ws.tradingDayCutoff : null, requestZone: timeZone });
    const route = ws ? `${ws.id}\n${r.platformCode || ""}` : null;
    const feedFrom = route && routes.has(route) ? routes.get(route) : earliest;
    return {
      ...r,
      day,
      workspaceId: ws ? ws.id : null,
      storeKnown: storeWs.has(key),
      feedFrom,
      inFeedWindow: Boolean(feedFrom && day && day >= feedFrom),
    };
  });
}

const round2 = (x) => Math.round(x * 100) / 100;

// Per (business day, location, platform code): counting orders, gross =
// Σ orderSubtotal, net = Σ (orderSubtotal − discount), cancelled (uploaded,
// not counted) and fully discounted (counting, net ≤ 0). classifyRows leaves
// out rows with no subtotal, so every counted order has one. Sorted by day,
// location id, platform code.
export function summarise(annotated) {
  const groups = new Map();
  for (const r of Array.isArray(annotated) ? annotated : []) {
    const key = `${r.day}\n${r.workspaceId || ""}\n${r.platformCode || ""}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        day: r.day, workspaceId: r.workspaceId, platformCode: r.platformCode, feedFrom: r.feedFrom,
        inFeedWindow: r.inFeedWindow, rows: 0, orders: 0, gross: 0, net: 0, cancelled: 0, fullyDiscounted: 0,
      };
      groups.set(key, g);
    }
    g.rows += 1;
    if (!r.counting) {
      g.cancelled += 1;
    } else {
      g.orders += 1;
      if (r.orderSubtotal != null) {
        const net = r.orderSubtotal - (r.discount == null ? 0 : r.discount);
        g.gross += r.orderSubtotal;
        g.net += net;
        if (net <= 0) g.fullyDiscounted += 1;
      }
    }
  }
  const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
  return [...groups.values()]
    .map((g) => ({ ...g, gross: round2(g.gross), net: round2(g.net) }))
    .sort((a, b) => cmp(a.day || "", b.day || "") || cmp(a.workspaceId || "", b.workspaceId || "") || cmp(a.platformCode || "", b.platformCode || ""));
}

// ── Uploading ───────────────────────────────────────────────────────────────
// Exactly the fields feedApi.importOrderChunk documents, nothing else.
export function toUploadRow(r) {
  return {
    providerOrderId: r.providerOrderId,
    orderCreatedAt: r.orderCreatedAt,
    state: r.state,
    platformCode: r.platformCode,
    providerStoreId: r.providerStoreId,
    providerBrandId: r.providerBrandId || "",
    posStoreRef: r.posStoreRef,
    storeName: r.storeName,
    brandName: r.brandName,
    orderSubtotal: r.orderSubtotal,
    discount: r.discount,
    aggregatorDiscount: r.aggregatorDiscount,
    merchantDiscount: r.merchantDiscount,
    totalCharges: r.totalCharges,
    totalTaxes: r.totalTaxes,
    orderTotal: r.orderTotal,
    paymentMode: r.paymentMode,
    orderType: r.orderType,
    deliveryType: r.deliveryType,
    orderRefId: r.orderRefId,
    externalPlatformId: r.externalPlatformId,
  };
}

// UTF-8 bytes of a string (no TextEncoder in this Jest environment).
export function utf8Length(s) {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      n += 4;
      i += 1;
    } else n += 3;
  }
  return n;
}

// Rows in file order → [{ rows: [uploadRow], fileRows: [n] }], each at most
// maxRows rows and (estimated on the wire) maxBytes. Sent one after another.
export function chunkRows(rows, { maxRows = MAX_CHUNK_ROWS, maxBytes = MAX_CHUNK_BYTES } = {}) {
  const chunks = [];
  let cur = null;
  for (const r of Array.isArray(rows) ? rows : []) {
    const up = toUploadRow(r);
    const size = utf8Length(JSON.stringify(up)) + ROW_WIRE_OVERHEAD;
    if (!cur || cur.rows.length >= maxRows || (cur.rows.length > 0 && cur.bytes + size > maxBytes)) {
      cur = { rows: [], fileRows: [], bytes: 64 };
      chunks.push(cur);
    }
    cur.rows.push(up);
    cur.fileRows.push(r.fileRow);
    cur.bytes += size;
  }
  return chunks.map(({ rows: chunkRowsList, fileRows }) => ({ rows: chunkRowsList, fileRows }));
}

const SUM_KEYS = ["received", "inserted", "updated", "unchanged", "storesNew", "daysRecomputed", "replacedDays"];
// [{ result: importOrderChunk's answer, fileRows }] → one summary. Counts are
// summed over the requests (a day split across two requests is recomputed,
// and counted, twice; the .NET host applies an import later and answers
// updated, daysRecomputed and replacedDays 0); rejected[].index becomes the
// row's file row number. warnings are the host's notes, English sentences
// shown as they come: every request is read on its own, so the same note
// comes back once per request, and each is kept once, in first-seen order.
export function mergeImportResults(parts) {
  const out = {
    requests: 0, received: 0, inserted: 0, updated: 0, unchanged: 0, rejected: [], storesNew: 0, daysRecomputed: 0, replacedDays: 0,
    warnings: [],
  };
  const seenWarnings = new Set();
  for (const part of Array.isArray(parts) ? parts : []) {
    const result = part && part.result;
    if (!result) continue;
    const fileRows = Array.isArray(part.fileRows) ? part.fileRows : [];
    out.requests += 1;
    for (const k of SUM_KEYS) out[k] += Number.isFinite(result[k]) ? result[k] : 0;
    for (const rj of Array.isArray(result.rejected) ? result.rejected : []) {
      const ok = rj && Number.isInteger(rj.index) && rj.index >= 0 && rj.index < fileRows.length;
      out.rejected.push({ fileRow: ok ? fileRows[rj.index] : null, reason: rj && typeof rj.reason === "string" ? rj.reason : "unknown" });
    }
    for (const w of Array.isArray(result.warnings) ? result.warnings : []) {
      if (typeof w === "string" && w.trim() !== "" && !seenWarnings.has(w)) {
        seenWarnings.add(w);
        out.warnings.push(w);
      }
    }
  }
  out.rejected.sort((a, b) => (a.fileRow == null ? Infinity : a.fileRow) - (b.fileRow == null ? Infinity : b.fileRow));
  return out;
}

// ── Defaults for the upload screen ──────────────────────────────────────────
// The zone shared by every location this connection's stores map to, else "".
export function suggestImportZone(stores, workspaces) {
  const wsById = new Map((Array.isArray(workspaces) ? workspaces : []).filter((w) => w && w.id).map((w) => [w.id, w]));
  const zones = new Set();
  for (const s of Array.isArray(stores) ? stores : []) {
    if (!s || !s.workspaceId) continue;
    const w = wsById.get(s.workspaceId);
    zones.add(w && isValidTimeZone(w.timeZone) ? w.timeZone.trim() : "");
  }
  return zones.size === 1 ? [...zones][0] : "";
}
// The one currency of the mapped locations (else of all active locations), or null.
export function commonCurrency(stores, workspaces) {
  const list = (Array.isArray(workspaces) ? workspaces : []).filter((w) => w && w.id);
  const mapped = new Set((Array.isArray(stores) ? stores : []).map((s) => s && s.workspaceId).filter(Boolean));
  const pickOne = (ws) => {
    const set = new Set(ws.map((w) => w.currency).filter((c) => typeof c === "string" && c));
    return set.size === 1 ? [...set][0] : null;
  };
  const fromMapped = pickOne(list.filter((w) => mapped.has(w.id)));
  return fromMapped || pickOne(list.filter((w) => !w.archivedAt));
}
